import React, { useEffect, useState, useCallback } from "react";
import { http, formatApiError, API } from "@/lib/api";
import TriagePanel from "@/pages/TriagePanel";

const STATUS_LABEL = {
    pending_review: "Pending review",
    quoted: "Quoted",
    approved: "Approved",
    in_progress: "In progress",
    completed: "Completed",
    paid: "Paid",
    cancelled: "Cancelled",
};

function StatusPill({ s }) {
    return <span className="text-xs uppercase tracking-widest text-gold">{STATUS_LABEL[s] || s}</span>;
}

export default function AdminPage() {
    const [tasks, setTasks] = useState([]);
    const [stats, setStats] = useState(null);
    const [selected, setSelected] = useState(null);
    const [quote, setQuote] = useState("");
    const [notes, setNotes] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [t, s] = await Promise.all([
                http.get("/admin/tasks"),
                http.get("/admin/stats"),
            ]);
            setTasks(t.data); setStats(s.data);
            if (selected) {
                const cur = t.data.find((x) => x.id === selected.id);
                setSelected(cur || null);
            }
        } catch (e) { setError(formatApiError(e)); }
    }, [selected]);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    function open(t) {
        setSelected(t);
        setQuote((t.butler_quote_amount ?? t.ai_quote_amount ?? "").toString());
        setNotes(t.butler_notes || "");
        setError("");
    }

    async function saveQuote() {
        if (!selected) return;
        setSaving(true); setError("");
        try {
            await http.post(`/admin/tasks/${selected.id}/quote`, {
                butler_quote_amount: parseFloat(quote),
                butler_notes: notes,
            });
            await load();
        } catch (e) { setError(formatApiError(e)); }
        finally { setSaving(false); }
    }

    async function setStatus(id, status) {
        try {
            await http.post(`/admin/tasks/${id}/status`, { status });
            await load();
        } catch (e) { setError(formatApiError(e)); }
    }

    async function deleteTask(id) {
        if (!window.confirm("Delete this task permanently? This cannot be undone.")) return;
        setError("");
        try {
            await http.delete(`/admin/tasks/${id}`);
            if (selected?.id === id) setSelected(null);
            await load();
        } catch (e) { setError(formatApiError(e)); }
    }

    return (
        <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
            <div className="label-eyebrow mb-3">/ Butler Desk</div>
            <h1 className="font-serif text-4xl mb-8">Operations</h1>

            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-10">
                    {[
                        ["Total", stats.total_tasks],
                        ["Pending", stats.pending_review],
                        ["Quoted", stats.quoted],
                        ["In progress", stats.in_progress],
                        ["Completed", stats.completed],
                        ["Paid", stats.paid_tasks],
                        ["Revenue", `$${stats.revenue}`],
                    ].map(([k, v]) => (
                        <div key={k} className="bg-slate-900/60 border border-white/10 p-4 rounded-sm">
                            <div className="text-xs text-slate-500 uppercase tracking-widest">{k}</div>
                            <div className="font-serif text-2xl mt-1 gold-text">{v}</div>
                        </div>
                    ))}
                </div>
            )}

            <TriagePanel />

            <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-7">
                    <div className="bg-slate-900/40 border border-white/5 rounded-sm overflow-hidden">
                        <div className="grid grid-cols-12 text-xs uppercase tracking-widest text-slate-500 border-b border-white/5 px-4 py-3">
                            <div className="col-span-5">Task</div>
                            <div className="col-span-3">Client</div>
                            <div className="col-span-2">Status</div>
                            <div className="col-span-2 text-right">Amount</div>
                        </div>
                        {tasks.length === 0 && <div className="px-4 py-8 text-slate-500">No tasks yet.</div>}
                        {tasks.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`admin-task-${t.id}`}
                                onClick={() => open(t)}
                                className={`w-full text-left grid grid-cols-12 px-4 py-3 border-b border-white/5 hover:bg-slate-900/60 ${selected?.id === t.id ? "bg-slate-900/80" : ""}`}
                            >
                                <div className="col-span-5">
                                    <div className="text-sm">{t.title}</div>
                                    <div className="text-xs text-slate-500">{t.category}</div>
                                </div>
                                <div className="col-span-3 text-sm text-slate-300">{t.client_email}</div>
                                <div className="col-span-2"><StatusPill s={t.status} /></div>
                                <div className="col-span-2 text-right gold-text font-serif">
                                    ${(t.final_amount ?? t.butler_quote_amount ?? t.ai_quote_amount)?.toFixed(0)}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                <aside className="lg:col-span-5">
                    {selected ? (
                        <div className="bg-slate-900/60 border border-white/10 p-6 rounded-sm sticky top-24">
                            <div className="label-eyebrow mb-2">/ Task #{selected.id.slice(0, 8)}</div>
                            <div className="font-serif text-2xl">{selected.title}</div>
                            <p className="text-sm text-slate-400 mt-3 leading-relaxed">{selected.description}</p>
                            <div className="text-xs text-slate-500 mt-3">
                                <div>{selected.address}</div>
                                {selected.preferred_date && <div>Preferred: {selected.preferred_date}</div>}
                            </div>

                            <div className="mt-5 text-sm">
                                <span className="text-slate-500">AI suggested </span>
                                <span className="gold-text font-medium">${selected.ai_quote_amount}</span>
                                <span className="text-slate-500"> · ~{selected.ai_estimated_hours}h</span>
                            </div>

                            <div className="mt-5 space-y-3">
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-slate-400">Butler quote (USD)</label>
                                    <input
                                        data-testid="admin-quote-input"
                                        type="number" step="0.01" value={quote} onChange={(e) => setQuote(e.target.value)}
                                        className="mt-2 w-full bg-slate-950 border border-white/10 rounded-sm px-3 py-2 focus:outline-none focus:border-gold"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-slate-400">Notes to client</label>
                                    <textarea
                                        data-testid="admin-notes-input"
                                        rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                                        className="mt-2 w-full bg-slate-950 border border-white/10 rounded-sm px-3 py-2 focus:outline-none focus:border-gold resize-none"
                                    />
                                </div>
                                {error && <div className="text-sm text-red-400">{error}</div>}
                                <button data-testid="admin-save-quote" onClick={saveQuote} disabled={saving} className="btn-gold w-full">
                                    {saving ? "Saving…" : "Send quote to client"}
                                </button>

                                <div className="flex flex-wrap gap-2 pt-2">
                                    {["in_progress", "completed", "cancelled"].map((s) => (
                                        <button
                                            key={s}
                                            data-testid={`admin-status-${s}`}
                                            onClick={() => setStatus(selected.id, s)}
                                            className="btn-ghost text-xs px-3 py-2"
                                        >
                                            Mark {s.replace("_", " ")}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    data-testid="admin-delete-task"
                                    onClick={() => deleteTask(selected.id)}
                                    className="mt-3 w-full text-xs px-3 py-2 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition"
                                >
                                    Delete task permanently
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-slate-900/40 border border-white/5 p-8 rounded-sm text-slate-500 text-sm">
                            Select a task to manage its quote.
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}
